import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  blogFetch,
  blogPollTenantJobs,
  isBlogArticleBusy,
  pollWhileVisible,
  blogFetchTenantStatus,
  blogPublishArticle,
  blogSetEnabled,
  blogUnpublishArticle,
  blogDeleteArticle,
  blogSaveCategory,
  blogDeleteCategory,
  blogSaveErrorMessage,
  blogFetchAutogen,
  blogSetAutogen,
  selectBlogArticlesLumioFirst,
  selectBlogTenantStatus,
  selectBlogTenantStatusError,
  selectBlogTenantStatusLoading,
  selectBlogAutogen,
  selectBlogAutogenError,
  selectBlogAutogenLoading,
  selectBlogCreditsBalance,
} from '@/store/slices/blog';
import { creditCostFor, fetchBlogCreditsPricing } from '@/store/slices/aiCredits';
import { selectBlogAuthorPref, selectOwnerProfilePrefs } from '@/store/slices/userPreferences';
import { WebsitePage } from '../website/components/WebsiteShell';
import { Panel } from '../../components/Panel';
import { SectionTitle } from '../../components/SectionTitle';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { Tab } from '../../components/Tab';
import { TabsRow } from '../../components/TabsRow';
import { Table, type TableColumn } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { Field } from '../../components/Field';
import { TextArea } from '../../components/TextArea';
import { Toast } from '../../components/Toast/Toast';
import { FtuxTarget } from '../../features/ftux/FtuxTarget';
import { ftuxSignal } from '../../features/ftux/signals';
import { useToast } from '../../hooks/useToast';
import { useRefetchOnVisible } from '../../hooks/useRefetchOnVisible';
import type { BlogArticle, BlogCategory } from '../../mock-data/types';

import { ROUTE } from '../../routes';
import { PlayersPagination } from '../players/components/PlayersPagination';
import { CATEGORY_NAME_MAX, slugify } from './slug';
import { latestUpdateFirst, pageOf } from './articlePages';
import { getNewArticles } from './newArticles';
import { formatArticleDate } from './articleDate';
import { autopilotByline, type AutopilotByline } from './byline';
import { GeneratingArticle, PendingBar, WritingPill } from './components/GeneratingArticle';
import { BlogEnableToggle } from './components/BlogEnableToggle';
import { AutopilotPanel } from './components/AutopilotPanel';
import {
  Section,
  HeaderRow,
  HeaderTitleGroup,
  TitleCell,
  ThumbBox,
  ThumbImg,
  TitleWrap,
  TitleLine,
  ArticleTitle,
  ArticleSlug,
  CellText,
  DateText,
  RowActions,
  ActionButton,
  DangerActionButton,
  DangerButton,
  ModalCopy,
  ModalArticleName,
  TablePanelBody,
  CategoryRow,
  CategoryInfo,
  CategoryName,
  CategoryDescription,
  CategoryList,
  CategoryActions,
  StartHerePill,
  StartHereDot,
  CountButton,
  ArticleListWrap,
  ArticleRowButton,
  RetryWrap,
  ActionReason,
  CategorySkeletons,
  ModalFields,
  PagerFooter,
} from './blog.styles';

type StatusFilter = 'all' | 'published' | 'draft';

/**
 * Every row is a real article now, including the ones being written.
 *
 * There used to be a second row KIND for a generation in flight, because there
 * was a window with no article to show: the owner's prompt went out and the
 * draft only existed once the writing finished. The service creates the draft
 * FIRST now and hangs the job off it, so that window is gone — and with it the
 * reason for a fake row. A row is writing when the service says its article has
 * a running job, which is a fact anyone can read from any device, rather than
 * something one browser tab was holding.
 */
type BlogRow = { article: BlogArticle };

type PendingArticleAction = {
  article: BlogArticle;
  action: 'publish' | 'unpublish' | 'delete';
};

type CategoryDraft = { id?: string; name: string; slug: string; description: string };

/** A tab return fires both visibilitychange and focus: settings re-reads on return closer together than this are one. */
const AUTOGEN_REREAD_MIN_GAP_MS = 5_000;

/**
 * Blog authoring home (`docs/blog-authoring-plan.md`): every article of the
 * active brand's casino blog in a table (title · category · status · updated ·
 * actions), filterable by status, plus the category (topic cluster) management
 * list. Publish/Unpublish, article deletes and category deletes all confirm
 * through the shared Modal, then toast. Deleting an article is the only one of
 * those that cannot be undone, and the only one the service refuses outright
 * while the article is published — the row disables it and says so rather than
 * letting the confirm dialog end in a 409. Data flows through the blog slice
 * (mock-backed until the blog API lands).
 */
export const PageBlog = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast, show } = useToast();

  const loaded = useAppSelector((state) => state.blog.loaded);
  const loading = useAppSelector((state) => state.blog.loading);
  // This session's Lumio drafts sit at the top, so a finished article lands
  // where the owner just watched its row being written. The table re-sorts its
  // rows by their last update (`latestUpdateFirst` below), which keeps them
  // there: the pass that just finished is the latest write the brand has.
  const articles = useAppSelector(selectBlogArticlesLumioFirst);
  // Keyed by article and read as a whole: the cells need to ask about a row at a
  // time, and a per-id selector cannot be called from inside a renderer.
  const jobs = useAppSelector((state) => state.blog.jobs);
  const categories = useAppSelector((state) => state.blog.categories);
  const saving = useAppSelector((state) => state.blog.saving);
  const fetchError = useAppSelector((state) => state.blog.error);

  // The blog's on/off switch reads a different service question than the list
  // (the tenant row, not its content), so it loads on its own guard. It is also
  // the only one of the two that can tell an unprovisioned brand from an empty
  // one — both list nothing — and the switch is what fixes the first.
  const tenantStatus = useAppSelector(selectBlogTenantStatus);
  const tenantStatusLoading = useAppSelector(selectBlogTenantStatusLoading);
  const tenantStatusError = useAppSelector(selectBlogTenantStatusError);

  React.useEffect(() => {
    // The error guard stops the retry loop a rejected fetch would otherwise
    // cause (loading flips back to false with loaded still false).
    if (!loaded && !loading && fetchError == null) void dispatch(blogFetch());
  }, [dispatch, loaded, loading, fetchError]);

  React.useEffect(() => {
    if (tenantStatus === null && !tenantStatusLoading && tenantStatusError == null) {
      void dispatch(blogFetchTenantStatus());
    }
  }, [dispatch, tenantStatus, tenantStatusLoading, tenantStatusError]);

  /*
   * Autopilot (`docs/auto-generation.md`): the settings read on its own guard,
   * like the switch above, and for the same reason: it is a third service
   * question, and its one special answer (a 404 from an older build, which
   * resolves rather than rejects) is what decides whether the panel exists.
   * The price on the panel comes from the same table the editor prices its
   * Generate buttons from, and the byline it will sign with is decided by
   * `autopilotByline`: the one the owner last saved an article under, else the
   * latest signed article's (left to the service), else the owner's profile
   * name, else none.
   */
  const autogen = useAppSelector(selectBlogAutogen);
  const autogenLoading = useAppSelector(selectBlogAutogenLoading);
  const autogenError = useAppSelector(selectBlogAutogenError);
  const blogPricing = useAppSelector((state) => state.aiCredits.blogPricing);
  const rememberedAuthor = useAppSelector(selectBlogAuthorPref);
  const ownerProfile = useAppSelector(selectOwnerProfilePrefs);
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  // Until both the preferences and the article list have loaded the byline is
  // unknown, not absent: the panel keeps the switch locked rather than promise
  // a byline it would not send.
  const byline = React.useMemo<AutopilotByline | undefined>(
    () =>
      prefsLoaded && loaded
        ? autopilotByline({ remembered: rememberedAuthor, articles, profile: ownerProfile })
        : undefined,
    [prefsLoaded, loaded, rememberedAuthor, articles, ownerProfile],
  );

  React.useEffect(() => {
    if (autogen === null && !autogenLoading && autogenError == null) void dispatch(blogFetchAutogen());
  }, [dispatch, autogen, autogenLoading, autogenError]);

  /*
   * The settings the slice already holds are read again when the page opens
   * and on every return to the tab. The slice outlives the page (it resets
   * only on a brand switch), and the service moves on without the studio: a
   * run publishes and moves the next one, a later run closes the credits
   * notice. Without this the panel showed the document of the first visit
   * until a reload. The guard above makes the first read; a held document is
   * replaced when the answer lands, so the panel never blanks meanwhile, and a
   * failed refresh keeps showing it.
   */
  const autogenHeld = autogen !== null && autogen.supported;
  const refreshAutogen = React.useCallback(() => {
    if (autogenHeld) void dispatch(blogFetchAutogen());
  }, [dispatch, autogenHeld]);
  const refreshedOnOpen = React.useRef(false);
  React.useEffect(() => {
    if (refreshedOnOpen.current) return;
    refreshedOnOpen.current = true;
    refreshAutogen();
  }, [refreshAutogen]);
  const refreshedOnReturnAt = React.useRef(0);
  const refreshOnReturn = React.useCallback(() => {
    const now = Date.now();
    if (now - refreshedOnReturnAt.current < AUTOGEN_REREAD_MIN_GAP_MS) return;
    refreshedOnReturnAt.current = now;
    refreshAutogen();
  }, [refreshAutogen]);
  useRefetchOnVisible(refreshOnReturn, autogenHeld);

  // The owner's AI-credits balance, as the header chip shows it: the panel says
  // "Paused" only while it is short of what the next article costs.
  const creditsBalance = useAppSelector(selectBlogCreditsBalance);

  React.useEffect(() => {
    void dispatch(fetchBlogCreditsPricing());
  }, [dispatch]);

  /*
   * The articles the Home Spotlight's "See articles" sent the owner here to
   * find: their rows carry a "New" badge. Read once per brand from
   * sessionStorage (`newArticles.ts`), which is not reactive and need not be:
   * the list is remounted by every navigation to it, and the ids only ever
   * change on a navigation (opening an article removes it, leaving the blog
   * area clears the lot). Keyed on the ACTIVE brand, the one the slice's
   * tenant is, which is the brand Home wrote them under.
   */
  const brandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  const newArticleIds = React.useMemo(() => new Set(brandId ? getNewArticles(brandId) : []), [brandId]);

  /**
   * Flip the Autopilot switch. Turning on sends the byline `autopilotByline`
   * settled on, the one the confirmation named (the panel has already
   * confirmed the cost with them): a named author as is, otherwise null, which
   * the client puts on the wire as a nameless author so a byline stored by an
   * earlier enable is cleared and the service falls back to the latest signed
   * article's, or none (the service reads a null as "leave it"). Turning off
   * sends the switch alone and leaves the stored byline as it is. The service
   * answers the settings that landed, and a refusal (no owner, suspended,
   * writer off) arrives as a rejection carrying its own copy.
   */
  const applyAutogen = async (next: boolean): Promise<void> => {
    // The panel keeps the switch locked until the byline is known, so an
    // enable always has one; this only keeps a stray call from guessing.
    if (next && byline === undefined) return;
    try {
      await dispatch(
        blogSetAutogen(next ? { enabled: true, author: byline?.author ?? null } : { enabled: false }),
      ).unwrap();
      show(next ? t('Autopilot is on') : t('Autopilot is off'), { icon: 'check' });
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  /*
   * What is being written for this brand, asked of the service rather than
   * remembered by this tab.
   *
   * It runs from the moment the list loads, not from the moment someone presses
   * Generate: the generations this page most needs to show are the ones it did
   * NOT start — another tab, another device, or a session closed hours ago — and
   * a page that only polled after its own click would never learn about them.
   * The first tick fires immediately so a reopened list is not blank for the
   * length of an interval.
   *
   * A brand with no tenant row is excluded: its list loads (empty), but every
   * request under `/tenants/{id}` 404s, so polling it would be a failing call
   * every three seconds for as long as the page is open. Only a KNOWN-absent
   * tenant stops it — while the tenant read is still in flight the poll runs,
   * because a tenant read that failed for its own reasons must not take the
   * generation watch down with it.
   */
  React.useEffect(() => {
    if (!loaded || tenantStatus?.provisioned === false) return undefined;

    void dispatch(blogPollTenantJobs());

    return pollWhileVisible(() => void dispatch(blogPollTenantJobs()));
  }, [dispatch, loaded, tenantStatus?.provisioned]);

  /*
   * A generation that ENDED means the list is now stale: a successful pass wrote
   * a title, a body and a category into the draft, and this page is still
   * showing the placeholder. Re-read once, when the count of running jobs drops
   * — not on every tick, which would refetch the whole list every couple of
   * seconds for as long as anything was being written.
   */
  const runningIds = Object.values(jobs)
    .filter((job) => job.status === 'running')
    .map((job) => job.articleId);
  const runningKey = [...runningIds].sort().join(',');
  const previousKey = React.useRef(runningKey);

  // A running job for an article this tab has never heard of. Another tab (or
  // Lumio, or another device) created the draft, and only that tab's store was
  // told — so without this the row appears here for the first time when the
  // generation ENDS, which is the opposite of what the mechanism is for.
  const missing = runningIds.some((id) => !articles.some((article) => article.id === id));

  React.useEffect(() => {
    // Keyed on WHICH articles are running rather than how many: one ending as
    // another starts leaves a count unchanged and used to skip the re-read.
    if (missing || runningKey !== previousKey.current) void dispatch(blogFetch());
    previousKey.current = runningKey;
  }, [dispatch, runningKey, missing]);

  const [filter, setFilter] = React.useState<StatusFilter>('all');

  /*
   * The page of the table the owner is on. It goes back to the first page
   * whenever what the table lists changes under it: another tab, another brand
   * in the top bar, or an article this tab just asked Lumio to write (the
   * newest of `generated`), because Lumio sends the owner to this list to
   * watch that article's row, which leads the first page. Both resets happen
   * while rendering, so no frame of the stale page is ever painted.
   */
  const latestGenerated = useAppSelector((state) => state.blog.generated[0] ?? '');
  const pageKey = `${brandId ?? ''}|${filter}|${latestGenerated}`;
  const [paging, setPaging] = React.useState({ key: pageKey, page: 1 });
  if (paging.key !== pageKey) setPaging({ key: pageKey, page: 1 });
  const requestedPage = paging.key === pageKey ? paging.page : 1;

  // Article publish/unpublish confirm: `pending` keeps the last article through
  // the modal's exit animation; `confirmOpen` alone drives visibility.
  const [pending, setPending] = React.useState<PendingArticleAction | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // "Turn the blog off?" confirm: see `requestEnabledChange` for why only that
  // direction asks.
  const [disableOpen, setDisableOpen] = React.useState(false);

  // Category editor modal + delete confirm.
  const [categoryDraft, setCategoryDraft] = React.useState<CategoryDraft | null>(null);
  const [categoryOpen, setCategoryOpen] = React.useState(false);
  const [deleteCategory, setDeleteCategory] = React.useState<BlogCategory | null>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  // "N articles" popup: the category whose articles are listed.
  const [articlesCategory, setArticlesCategory] = React.useState<BlogCategory | null>(null);
  const [articlesOpen, setArticlesOpen] = React.useState(false);

  const published = articles.filter((article) => article.status === 'published');
  const drafts = articles.filter((article) => article.status === 'draft');
  const visible = filter === 'all' ? articles : filter === 'published' ? published : drafts;

  // The slice's own definition of "being written", rather than a third copy of
  // the rule: a job's PRESENCE is not the answer, its status is.
  const isWriting = (id: string): boolean => isBlogArticleBusy(jobs, id);

  // Most recently updated first, the date the Updated column shows: the
  // service lists articles oldest first, which put a busy brand's latest
  // article at the bottom of an ever longer table. An article being written
  // leads the table all the same: it is the one thing on this page that is
  // changing while the owner watches. It needs no filtering out of Published:
  // a draft being written is a draft, and the status filter already says so.
  const ordered = latestUpdateFirst(visible);
  const writing = ordered.filter((article) => isWriting(article.id));
  const rows: BlogRow[] = [
    ...writing.map((article): BlogRow => ({ article })),
    ...ordered.filter((article) => !isWriting(article.id)).map((article): BlogRow => ({ article })),
  ];

  // Ten rows at a time. Only the table is paged: the tab counts, the category
  // counts and the byline all read the full list above.
  const shown = pageOf(rows, requestedPage);

  // A page that no longer exists (its last article was deleted or published
  // away from Drafts) shows the last page, and that is now the page the owner
  // is on: kept as asked, it would come back by itself, without a click, the
  // next time the list grows. Only once loaded: a re-read keeps the articles in
  // hand until the new ones replace them, so a loaded list never shrinks for a
  // moment, but an unloaded one is empty.
  if (loaded && paging.key === pageKey && shown.page !== paging.page) {
    setPaging({ key: pageKey, page: shown.page });
  }

  // Position among the writing rows, so two never animate in lockstep.
  const writingIndex = new Map(writing.map((article, index) => [article.id, index]));

  const categoryName = (id: string): string => categories.find((c) => c.id === id)?.name ?? id;
  const articleCount = (categoryId: string): number => articles.filter((a) => a.categoryId === categoryId).length;

  const askConfirm = (article: BlogArticle, action: PendingArticleAction['action']): void => {
    setPending({ article, action });
    setConfirmOpen(true);
  };

  const applyPending = async (): Promise<void> => {
    if (!pending) return;
    setConfirmOpen(false);
    try {
      if (pending.action === 'publish') {
        await dispatch(blogPublishArticle(pending.article.id)).unwrap();
        show(t('Article published'), { icon: 'check' });
      } else if (pending.action === 'unpublish') {
        await dispatch(blogUnpublishArticle(pending.article.id)).unwrap();
        show(t('Article unpublished'), { icon: 'check' });
      } else {
        await dispatch(blogDeleteArticle(pending.article.id)).unwrap();
        show(t('Article deleted'), { icon: 'check' });
      }
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  /**
   * Write the blog's on/off state and report what actually landed.
   *
   * The thunk re-reads the tenant afterwards, so the toast describes the state
   * the service is really in rather than the one that was requested: enabling
   * can be refused (a ban, or the re-enable window) and that refusal arrives as
   * a rejection carrying the service's own explanation.
   */
  const applyEnabled = async (next: boolean): Promise<void> => {
    setDisableOpen(false);
    try {
      const status = await dispatch(blogSetEnabled(next)).unwrap();
      show(status.enabled ? t('The blog is live') : t('The blog is off'), { icon: 'check' });
      // The lists have to be re-read, and nothing else will do it: the guard
      // effect above deliberately never retries after a failure, and turning
      // the blog ON is exactly the case where the earlier read failed because
      // there was no tenant to read. Without this the owner who has just
      // created their tenant is still looking at the error they were stuck on.
      // `blogFetch.pending` clears `state.blog.error`, so the page leaves its
      // error branch as soon as this goes out.
      void dispatch(blogFetch());
      // The Autopilot settings answer differently once the tenant exists (a
      // brand with no tenant read them as the locked defaults), so they are
      // re-read on the same occasion.
      void dispatch(blogFetchAutogen());
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  /**
   * Turning the blog OFF confirms first; turning it back on does not.
   *
   * The two directions are not equally reversible: the service allows one
   * off-to-on transition per re-enable window (24 hours by default), so a stray
   * click on the switch can take a live blog down for a day. Turning it on is
   * instant and harmless.
   */
  const requestEnabledChange = (next: boolean): void => {
    if (next) {
      // FTUX: the switch pressed on ends the blog-on piece (a no-op outside an FTUX step).
      ftuxSignal('blog-switch-pressed');
      void applyEnabled(true);
    } else {
      setDisableOpen(true);
    }
  };

  const openCategoryEditor = (category?: BlogCategory): void => {
    setCategoryDraft(
      category
        ? { id: category.id, name: category.name, slug: category.slug, description: category.description }
        : { name: '', slug: '', description: '' },
    );
    setCategoryOpen(true);
  };

  const saveCategory = async (): Promise<void> => {
    if (!categoryDraft || !categoryDraft.name.trim()) {
      show(t('Add a category name before saving'));
      return;
    }
    setCategoryOpen(false);
    try {
      // The server generates the slug from the name and ignores this value; the
      // derived slug is sent anyway (harmless) and the reducer adopts the saved
      // category verbatim, server slug included.
      await dispatch(
        blogSaveCategory({ ...categoryDraft, slug: slugify(categoryDraft.slug) || slugify(categoryDraft.name) }),
      ).unwrap();
      show(t('Category saved'), { icon: 'check' });
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  const confirmDeleteCategory = async (): Promise<void> => {
    if (!deleteCategory) return;
    setDeleteOpen(false);
    try {
      await dispatch(blogDeleteCategory(deleteCategory.id)).unwrap();
      show(t('Category deleted'), { icon: 'check' });
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  const renderTitle = (article: BlogArticle): React.ReactElement => (
    <TitleCell>
      {/* Decorative echo of the hero; the title beside it carries the name. */}
      <ThumbBox>{article.hero ? <ThumbImg src={article.hero.url} alt="" /> : null}</ThumbBox>
      <TitleWrap>
        <TitleLine>
          <ArticleTitle text={article.title} tag="span" />
          {article.pillar ? (
            <StartHerePill>
              <StartHereDot aria-hidden="true" />
              {t('Start here')}
            </StartHerePill>
          ) : null}
          {/* Permanent: an article the blog wrote for itself stays marked as
              such, however many times the owner edits it afterwards. */}
          {article.origin === 'autopilot' ? <Badge label={t('Autopilot')} tone="accent" /> : null}
          {/* Until it is opened, or the owner leaves the blog area. */}
          {newArticleIds.has(article.id) ? <Badge label={t('New')} tone="success" /> : null}
        </TitleLine>
        <ArticleSlug>/{article.slug}</ArticleSlug>
      </TitleWrap>
    </TitleCell>
  );

  const renderActions = (article: BlogArticle): React.ReactElement => (
    <RowActions>
      {/* Mock stage: View opens the in-studio preview for BOTH drafts and
          published articles; once the casino public URL exists in config,
          published View switches to it (docs/blog-authoring-plan.md). */}
      <ActionButton type="button" onClick={() => navigate(ROUTE.blogPreview(article.id))}>
        {t('View')}
      </ActionButton>
      <ActionButton type="button" onClick={() => navigate(ROUTE.blogEdit(article.id))}>
        {t('Edit')}
      </ActionButton>
      {article.status === 'published' ? (
        <ActionButton type="button" onClick={() => askConfirm(article, 'unpublish')}>
          {t('Unpublish')}
        </ActionButton>
      ) : (
        <ActionButton type="button" onClick={() => askConfirm(article, 'publish')}>
          {t('Publish')}
        </ActionButton>
      )}
      {/* The service refuses to delete a published article outright, so the
          action is disabled rather than offered and then refused: the row says
          what has to happen first, before anyone confirms anything. It stays in
          the row either way so the action column does not reflow between a
          draft and a published article. Same shape as the category delete. */}
      <DangerActionButton
        type="button"
        disabled={article.status === 'published'}
        title={article.status === 'published' ? t('Unpublish the article before deleting it') : undefined}
        onClick={() => askConfirm(article, 'delete')}
      >
        {t('Delete')}
      </DangerActionButton>
    </RowActions>
  );

  /*
   * What the page says when the load failed, in the SERVICE's own words.
   *
   * The slice already put the service's explanation in `state.blog.error`
   * (`blogFetchErrorMessage`) and this page used to throw it away for one fixed
   * sentence, so a 401, a 403 "You do not have access to this tenant", a 404
   * "Tenant not found" and a 500 all rendered identically. The copy is already
   * a locale key, so it goes through `<Text>`'s `text` prop like any other
   * string (the same idiom the switch uses for this exact value). The fixed
   * sentence stays as the fallback for a rejection that carried no copy at all.
   */
  const loadErrorText = fetchError != null && fetchError.trim() !== '' ? fetchError : 'The blog could not be loaded.';

  /*
   * Retry re-reads BOTH questions, not just the lists.
   *
   * The two calls fail together in practice — a lapsed session, a wrong base
   * URL, a tenant that is not this owner's — but only the list read has a guard
   * effect that would start again. So a Retry that dispatched `blogFetch` alone
   * could clear the page while leaving the switch still showing the tenant
   * read's failure, with nothing left on screen that would ever ask again.
   */
  const retryLoad = (): void => {
    void dispatch(blogFetch());
    void dispatch(blogFetchTenantStatus());
  };

  /*
   * The blog has never been turned on, so there is no tenant to write into.
   *
   * It is NOT the same as a failed load, and the difference is the whole of
   * SB-239: the lists load fine here (the tenant's own 404 reads as an empty
   * blog), they are just empty forever until the switch is flipped. An owner
   * told "No categories yet. Every article needs one." in this state follows
   * that instruction into a 404 reading "Tenant not found".
   *
   * The sentence itself is repeated at both use sites rather than hoisted into
   * a const: the i18n extractor harvests string LITERALS out of `text=` and
   * `t()`, so a shared identifier would leave the key unextracted.
   */
  const notTurnedOn = tenantStatus?.provisioned === false;

  const columns: TableColumn<BlogRow>[] = [
    {
      id: 'title',
      header: t('Title'),
      cell: (row) =>
        isWriting(row.article.id) ? (
          // The placeholder title IS the owner's brief — the service titles the
          // draft from their own words precisely so this row can say WHICH
          // article is being written.
          <GeneratingArticle topic={row.article.title} index={writingIndex.get(row.article.id) ?? 0} />
        ) : (
          renderTitle(row.article)
        ),
    },
    {
      id: 'category',
      header: t('Category'),
      width: '170px',
      cell: (row) =>
        isWriting(row.article.id) ? (
          <PendingBar aria-hidden="true" />
        ) : (
          <CellText text={categoryName(row.article.categoryId)} tag="span" />
        ),
    },
    {
      id: 'status',
      header: t('Status'),
      width: '120px',
      cell: (row) =>
        isWriting(row.article.id) ? (
          // Its own pill, not a Badge: the row is mid-flight, so it carries the
          // list's live-marker treatment rather than a settled status word.
          <WritingPill text={t('Writing')} tag="span" />
        ) : row.article.status === 'published' ? (
          <Badge label={t('Published')} tone="success" />
        ) : (
          <Badge label={t('Draft')} tone="neutral" />
        ),
    },
    {
      id: 'updated',
      header: t('Updated'),
      width: '130px',
      cell: (row) =>
        isWriting(row.article.id) ? (
          <PendingBar aria-hidden="true" />
        ) : (
          <DateText>{formatArticleDate(row.article.updatedAt, i18n.language, 'short')}</DateText>
        ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: '230px',
      // Publishing or deleting an article mid-generation would race the pass
      // that is writing into it, so the row offers nothing until it lands.
      cell: (row) => (isWriting(row.article.id) ? null : renderActions(row.article)),
    },
  ];

  return (
    <WebsitePage>
      <Section>
        <HeaderRow>
          <HeaderTitleGroup>
            <SectionTitle title="Blog" />
            <BlogEnableToggle
              status={tenantStatus}
              error={tenantStatusError}
              busy={saving}
              onChange={requestEnabledChange}
            />
          </HeaderTitleGroup>
          {/* Where the FTUX's new-article piece lights the button (inert outside an FTUX step). */}
          <FtuxTarget id="blog-new-article">
            <Button label={t('New article')} icon="plus" onClick={() => navigate(ROUTE.blogNew())} />
          </FtuxTarget>
        </HeaderRow>

        {/* Hidden on its own when the service build has no Autopilot (a 404
            resolves to `supported: false`), so this is safe against an older
            service. The price is the pricing table's `article` row, the same
            number the editor puts on its Generate button. */}
        <AutopilotPanel
          autogen={autogen}
          error={autogenError}
          tenantStatus={tenantStatus}
          price={creditCostFor(blogPricing, 'article')}
          creditsBalance={creditsBalance}
          byline={byline}
          busy={saving}
          onChange={(next) => void applyAutogen(next)}
          // FTUX: the confirmation opening ends the Autopilot piece (a no-op outside an FTUX step).
          onConfirmOpen={() => ftuxSignal('blog-autopilot-confirm-opened')}
        />

        <TabsRow>
          {/* Writing articles are real articles now, so they are already in these
              counts — there is nothing to add for them. */}
          <Tab
            id="all"
            label={t('All')}
            count={loaded ? articles.length : undefined}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <Tab
            id="published"
            label={t('Published')}
            count={loaded ? published.length : undefined}
            active={filter === 'published'}
            onClick={() => setFilter('published')}
          />
          <Tab
            id="drafts"
            label={t('Drafts')}
            count={loaded ? drafts.length : undefined}
            active={filter === 'draft'}
            onClick={() => setFilter('draft')}
          />
        </TabsRow>

        <Panel>
          <TablePanelBody>
            {/* A failed LIST fetch says nothing about a write, so articles
                already in hand still show — including one being written, which
                the owner was told to watch for here. A missing brand fails the
                fetch on its own and leaves nothing, which is the error case. */}
            {fetchError != null && articles.length === 0 ? (
              <>
                <EmptyState text={loadErrorText} />
                <RetryWrap>
                  <Button label={t('Retry')} variant="outline" onClick={retryLoad} />
                </RetryWrap>
              </>
            ) : loaded && rows.length === 0 ? (
              <EmptyState text="No articles in this view yet." />
            ) : (
              <Table
                columns={columns}
                rows={shown.items}
                rowKey={(row) => row.article.id}
                label={t('Blog articles')}
                // A row already being written is real content on screen, so the
                // table shows it instead of the load skeleton.
                loading={!loaded && rows.length === 0}
                skeletonRows={5}
              />
            )}
          </TablePanelBody>
          {/* Only when there is a second page: more than ten rows also means the
              table, not an empty or failed state, is what is on screen. */}
          {shown.pageCount > 1 ? (
            <PagerFooter>
              <PlayersPagination
                page={shown.page}
                pageCount={shown.pageCount}
                rangeStart={shown.rangeStart}
                rangeEnd={shown.rangeEnd}
                total={shown.total}
                onPageChange={(next) => setPaging({ key: pageKey, page: next })}
              />
            </PagerFooter>
          ) : null}
        </Panel>

        <HeaderRow>
          <SectionTitle title={t('Categories')} />
          {/* Offered whenever it COULD land. A load that failed for an unknown
              reason is not proof that a write would be refused, so a blipped
              read must not take the action away; a tenant that is known not to
              exist is proof, and the reason is on the wrapper because a disabled
              button surfaces no tooltip of its own. */}
          <ActionReason
            title={
              notTurnedOn ? t("This brand's blog is not turned on yet. Use the switch beside the page title.") : undefined
            }
          >
            <Button
              label={t('New category')}
              icon="plus"
              variant="outline"
              disabled={notTurnedOn}
              onClick={() => openCategoryEditor()}
            />
          </ActionReason>
        </HeaderRow>

        <Panel>
          {/* Four different empties, and the panel used to show the same blank
              for all of them: with `loaded === false` it fell through to an
              EMPTY CategoryList, no message at all. That silence is what the
              reporter saw. Content in hand still wins over any state message,
              as in the article panel above. */}
          {fetchError != null && categories.length === 0 ? (
            <>
              <EmptyState text={loadErrorText} />
              <RetryWrap>
                <Button label={t('Retry')} variant="outline" onClick={retryLoad} />
              </RetryWrap>
            </>
          ) : notTurnedOn && categories.length === 0 ? (
            <EmptyState text="This brand's blog is not turned on yet. Use the switch beside the page title." />
          ) : !loaded && categories.length === 0 ? (
            <CategorySkeletons>
              <Skeleton height={22} block />
              <Skeleton height={22} block />
              <Skeleton height={22} block />
            </CategorySkeletons>
          ) : categories.length === 0 ? (
            <EmptyState text="No categories yet. Every article needs one." />
          ) : (
            <CategoryList>
              {categories.map((category) => (
                <CategoryRow key={category.id}>
                  <CategoryInfo>
                    <TitleLine>
                      <CategoryName text={category.name} tag="span" />
                      <ArticleSlug>/{category.slug}</ArticleSlug>
                    </TitleLine>
                    <CategoryDescription text={category.description} tag="span" />
                  </CategoryInfo>
                  <CategoryActions>
                    <CountButton
                      type="button"
                      disabled={articleCount(category.id) === 0}
                      onClick={() => {
                        setArticlesCategory(category);
                        setArticlesOpen(true);
                      }}
                    >
                      {t('{{count}} articles', { count: articleCount(category.id) })}
                    </CountButton>
                    <ActionButton type="button" onClick={() => openCategoryEditor(category)}>
                      {t('Edit')}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      disabled={articleCount(category.id) > 0}
                      title={
                        articleCount(category.id) > 0
                          ? t('Move its articles to another category first')
                          : undefined
                      }
                      onClick={() => {
                        setDeleteCategory(category);
                        setDeleteOpen(true);
                      }}
                    >
                      {t('Delete')}
                    </ActionButton>
                  </CategoryActions>
                </CategoryRow>
              ))}
            </CategoryList>
          )}
        </Panel>
      </Section>

      {/* Publish / unpublish / delete confirm. Delete is the one that cannot be
          taken back, so it says so in as many words and its button carries the
          destructive treatment — the other two are reversible from this list. */}
      <Modal
        open={confirmOpen}
        title={
          pending?.action === 'delete'
            ? t('Delete this article?')
            : pending?.action === 'unpublish'
              ? t('Unpublish this article?')
              : t('Publish this article?')
        }
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setConfirmOpen(false)} />
            {pending?.action === 'delete' ? (
              <DangerButton type="button" disabled={saving} onClick={() => void applyPending()}>
                {t('Delete')}
              </DangerButton>
            ) : pending?.action === 'unpublish' ? (
              <Button label={t('Unpublish')} disabled={saving} onClick={() => void applyPending()} />
            ) : (
              <Button label={t('Publish')} disabled={saving} onClick={() => void applyPending()} />
            )}
          </>
        }
      >
        {pending ? <ModalArticleName text={pending.article.title} tag="paragraph" /> : null}
        {pending?.action === 'delete' ? (
          <ModalCopy
            text="This article will be deleted permanently, along with everything written in it. This cannot be undone."
            tag="paragraph"
          />
        ) : pending?.action === 'unpublish' ? (
          <ModalCopy
            text="The article will be removed from your casino blog and its public URL will stop working."
            tag="paragraph"
          />
        ) : (
          <ModalCopy text="The article will be visible on your casino blog right away." tag="paragraph" />
        )}
      </Modal>

      {/* Turn the blog off confirm. The re-enable window is the whole reason
          this step exists, so it is the second paragraph, not a footnote. */}
      <Modal
        open={disableOpen}
        title={t('Turn the blog off?')}
        onClose={() => setDisableOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setDisableOpen(false)} />
            <Button label={t('Turn off')} disabled={saving} onClick={() => void applyEnabled(false)} />
          </>
        }
      >
        <ModalCopy
          text="Your casino blog stops being served right away. Nothing is deleted, but every one of its public pages answers not found until you turn it back on."
          tag="paragraph"
        />
        <ModalCopy
          text="Turning it back on can be refused for up to 24 hours after the last time the blog was enabled."
          tag="paragraph"
        />
      </Modal>

      {/* Category create/edit */}
      <Modal
        open={categoryOpen}
        title={categoryDraft?.id ? t('Edit category') : t('New category')}
        onClose={() => setCategoryOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setCategoryOpen(false)} />
            <Button label={t('Save')} disabled={saving} onClick={() => void saveCategory()} />
          </>
        }
      >
        {categoryDraft ? (
          <ModalFields>
            <Field
              label={t('Name')}
              maxLength={CATEGORY_NAME_MAX}
              value={categoryDraft.name}
              onChange={(name) =>
                // The slug is never user-editable: it follows the name (the server
                // generates the authoritative slug and ignores the client's).
                setCategoryDraft((prev) => (prev ? { ...prev, name, slug: slugify(name) } : prev))
              }
            />
            {/* Read-only: the slug is auto-generated from the name. */}
            <Field label={t('Slug')} value={categoryDraft.slug} disabled onChange={() => undefined} />
            <TextArea
              label={t('Description')}
              rows={3}
              value={categoryDraft.description}
              onChange={(description) => setCategoryDraft((prev) => (prev ? { ...prev, description } : prev))}
              placeholder={t('Shown as the intro of the public category page')}
            />
          </ModalFields>
        ) : null}
      </Modal>

      {/* Category delete confirm */}
      <Modal
        open={deleteOpen}
        title={t('Delete this category?')}
        onClose={() => setDeleteOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setDeleteOpen(false)} />
            <Button label={t('Delete')} disabled={saving} onClick={() => void confirmDeleteCategory()} />
          </>
        }
      >
        {deleteCategory ? <ModalArticleName text={deleteCategory.name} tag="paragraph" /> : null}
        <ModalCopy text="The category page disappears from your casino blog. Articles are not deleted." tag="paragraph" />
      </Modal>

      {/* Category articles popup: reading order (pillar first), row -> editor. */}
      <Modal
        open={articlesOpen}
        title={articlesCategory?.name ?? ''}
        onClose={() => setArticlesOpen(false)}
      >
        <ArticleListWrap>
          {articles
            .filter((article) => article.categoryId === articlesCategory?.id)
            .sort(
              (a, b) =>
                Number(b.pillar) - Number(a.pillar) || a.sequence - b.sequence || a.title.localeCompare(b.title),
            )
            .map((article) => (
              <ArticleRowButton
                key={article.id}
                type="button"
                onClick={() => {
                  setArticlesOpen(false);
                  navigate(ROUTE.blogEdit(article.id));
                }}
              >
                <TitleWrap>
                  <TitleLine>
                    <ArticleTitle text={article.title} tag="span" />
                    {article.pillar ? (
              <StartHerePill>
                <StartHereDot aria-hidden="true" />
                {t('Start here')}
              </StartHerePill>
            ) : null}
                    {article.origin === 'autopilot' ? <Badge label={t('Autopilot')} tone="accent" /> : null}
                  </TitleLine>
                  <ArticleSlug>/{article.slug}</ArticleSlug>
                </TitleWrap>
                {article.status === 'published' ? (
                  <Badge label={t('Published')} tone="success" />
                ) : (
                  <Badge label={t('Draft')} tone="neutral" />
                )}
              </ArticleRowButton>
            ))}
        </ArticleListWrap>
      </Modal>

      <Toast toast={toast} />
    </WebsitePage>
  );
};

export default PageBlog;
